import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
  CartErrorEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

const OCS_CART_DRAWER_FRAGMENT_SECTION_ID = 'ocs-cart-drawer-fragment';
const CART_DRAWER_SELECTOR = 'ocs-cart-drawer-component, cart-drawer-component';

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #quantityRecoveryVersion = 0;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.#publishStepAnalytics(line, quantity);

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    // The drawer must never infer its next layout from current DOM rows. Keep
    // the line in its loading state until Shopify returns the authoritative
    // Liquid section. Preserve the approved cart-page removal animation below.
    if (this.closest('ocs-cart-drawer-component, cart-drawer-component')) return;

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Publish one quantity-stepper event — defects G18 and G19.
   *
   * Only a full line clear published anything before this: `#prepareRemoveAnalytics`
   * returns nothing unless `action === 'clear'`, so pressing + or − was silent.
   * Production published on every step, as add_to_cart or remove_from_cart
   * according to the direction, carrying the delta rather than the new total.
   *
   * A step down to zero is not handled here. It arrives as `quantity === 0` and
   * goes to `onLineItemRemove`, which publishes the whole line — which is also
   * what production's rule "decrement only fires when the current value is not
   * 1" amounted to.
   *
   * @param {number} line - The 1-based line index.
   * @param {number} quantity - The line's new total quantity.
   */
  #publishStepAnalytics(line, quantity) {
    const row = this.refs.cartItemRows[line - 1];
    const dataset = row?.dataset;
    if (!dataset) return;

    const previous = Number(dataset.ocsQuantity);
    if (!Number.isInteger(previous) || previous <= 0) return;

    const delta = quantity - previous;
    if (!Number.isInteger(delta) || delta === 0) return;

    const api = /** @type {any} */ (window).OCSAnalyticsEvents;
    if (
      !api ||
      typeof api.createCartStepPayload !== 'function' ||
      typeof api.publishOcsAnalyticsEvent !== 'function'
    ) {
      return;
    }

    try {
      const payload = api.createCartStepPayload(
        {
          itemName: dataset.ocsItemName,
          itemVariant: dataset.ocsItemVariant,
          priceCents: dataset.ocsPriceCents,
          quantity: Math.abs(delta),
          itemId: dataset.ocsPropertyItemId,
          category: dataset.ocsPropertyItemCategory,
          category2: dataset.ocsPropertyItemCategory2,
          category3: dataset.ocsPropertyItemCategory3,
          brand: dataset.ocsPropertyItemBrand,
          ontarioGrown: dataset.ocsPropertyOntarioGrown,
          craft: dataset.ocsPropertyCraft,
          priceDrop: dataset.ocsPropertyPriceDrop,
          new: dataset.ocsPropertyNew,
          itemAvailability: dataset.ocsItemAvailability,
        },
        'Cart'
      );
      if (!payload) return;

      if (delta > 0 && payload.items?.[0]) {
        payload.items[0].item_availability = dataset.ocsItemAvailability || '';
      }
      api.publishOcsAnalyticsEvent(delta > 0 ? 'ocs:add_to_cart' : 'ocs:remove_from_cart', payload);
    } catch (_) {}
  }

  /**
   * Capture the exact removed-line dataset and build its immutable payload before
   * the cart request or page removal animation can mutate the row.
   * @param {{ line: number, quantity: number, action: string }} config
   * @param {string|undefined} lineItemKey
   * @returns {{ api: any, payload: object|null }}
   */
  #prepareRemoveAnalytics(config, lineItemKey) {
    if (config.action !== 'clear' || config.quantity !== 0) return { api: null, payload: null };

    const row = this.refs.cartItemRows[config.line - 1];
    if (!row || typeof row !== 'object' || !row.dataset || typeof row.dataset !== 'object') {
      return { api: null, payload: null };
    }

    const dataset = row.dataset;
    /** @type {Record<string, string|undefined>} */
    const snapshotValues = {
      key: lineItemKey,
      itemName: dataset.ocsItemName,
      itemVariant: dataset.ocsItemVariant,
      priceCents: dataset.ocsPriceCents,
      quantity: dataset.ocsQuantity,
      itemId: dataset.ocsPropertyItemId,
      category: dataset.ocsPropertyItemCategory,
      category2: dataset.ocsPropertyItemCategory2,
      category3: dataset.ocsPropertyItemCategory3,
      brand: dataset.ocsPropertyItemBrand,
      ontarioGrown: dataset.ocsPropertyOntarioGrown,
      craft: dataset.ocsPropertyCraft,
      priceDrop: dataset.ocsPropertyPriceDrop,
      new: dataset.ocsPropertyNew,
    };
    if (dataset.ocsPropertyItemListName !== undefined) {
      snapshotValues.itemListName = dataset.ocsPropertyItemListName;
    }
    const snapshot = Object.freeze(snapshotValues);

    if (typeof snapshot.key !== 'string' || snapshot.key.trim().length === 0) {
      return { api: null, payload: null };
    }

    const api = /** @type {any} */ (window).OCSAnalyticsEvents;
    if (
      !api ||
      typeof api.createRemoveFromCartPayload !== 'function' ||
      typeof api.publishOcsAnalyticsEvent !== 'function'
    ) {
      return { api: null, payload: null };
    }

    try {
      const payload = api.createRemoveFromCartPayload(snapshot, 'Cart');
      if (!payload) return { api, payload: null };

      for (const item of payload.items) Object.freeze(item);
      Object.freeze(payload.items);
      return Object.freeze({ api, payload: Object.freeze(payload) });
    } catch (_) {
      return { api, payload: null };
    }
  }

  /**
   * Publish one enriched remove event without interrupting cart UI work.
   * @param {{ api: any, payload: object|null }} analytics
   */
  #publishRemoveAnalytics(analytics) {
    if (!analytics.api || !analytics.payload) return;

    try {
      analytics.api.publishOcsAnalyticsEvent('ocs:remove_from_cart', analytics.payload);
    } catch (_) {}
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const lineItemKey = this.refs.cartItemRows[config.line - 1]?.dataset.key;
    const removeAnalytics = this.#prepareRemoveAnalytics(config, lineItemKey);
    this.#publishRemoveAnalytics(removeAnalytics);

    // A newer cart mutation invalidates any in-flight error recovery fetch.
    this.#quantityRecoveryVersion += 1;

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const useDrawerFragment = config.action === 'change' && quantity > 0;
    const sectionsToUpdate = this.#getSectionsToUpdate(useDrawerFragment);
    const body = JSON.stringify({
      ...(lineItemKey ? { id: lineItemKey } : { line }),
      quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text().then((text) => ({ ok: response.ok, text }));
      })
      .then(({ ok, text }) => {
        const parsedResponseText = JSON.parse(text);

        // Shopify's Ajax Cart API can reject a change (e.g. over-inventory) with a 422
        // whose body has no top-level `errors` key, only `status`/`message`/`description`.
        // Treat a non-ok response, or either error shape, as a failure.
        if (!ok || parsedResponseText.errors || parsedResponseText.description) {
          resetShimmer(this);
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        resetShimmer(this);

        const currentSectionHTML = this.#getSectionHTML(parsedResponseText.sections, useDrawerFragment);
        const hasCurrentSectionHTML = typeof currentSectionHTML === 'string' && currentSectionHTML.length > 0;
        const newSectionHTML = new DOMParser().parseFromString(
          hasCurrentSectionHTML ? currentSectionHTML : '',
          'text/html'
        );

        // Prefer the Ajax Cart API's canonical cart quantity. Drawer renders don't
        // include the cart-page-only [ref="cartItemCount"], so relying on that ref
        // makes non-empty drawer removals broadcast itemCount: 0.
        const apiCartItemCount = Number(parsedResponseText.item_count);
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = Number.isFinite(apiCartItemCount)
          ? apiCartItemCount
          : newCartHiddenItemCount
            ? parseInt(newCartHiddenItemCount, 10)
            : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            usesCartDrawerFragment: useDrawerFragment,
            sections: parsedResponseText.sections,
          })
        );

        if (hasCurrentSectionHTML) {
          this.#morphCartItems(currentSectionHTML, useDrawerFragment);
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false });
        }

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        // The drawer keeps the authoritative Liquid row in place while a
        // mutation is pending. Always announce transport/parse failures so its
        // loading state is cleared even when Shopify returns no cart payload.
        this.dispatchEvent(new CartErrorEvent(this.sectionId, '', '', ''));
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} [parsedResponseText.message] - The error title from the server response.
   * @param {string} [parsedResponseText.description] - The error description from the server response (e.g. over-inventory 422).
   * @param {string} [parsedResponseText.errors] - The errors from the server response (legacy shape).
   */
  #handleCartError = (line, parsedResponseText) => {
    // Prefer the over-inventory 422 shape's `description`, then the legacy `errors` shape,
    // then fall back to the generic `message`.
    const message = parsedResponseText.description || parsedResponseText.errors || parsedResponseText.message || '';

    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input[data-cart-quantity]');

    if (quantityInput instanceof HTMLInputElement) {
      // Restore immediately from the last committed cart quantity. The input's
      // defaultValue can be stale after a prior in-place section morph.
      this.#setRejectedQuantity(quantitySelector, quantityInput, quantityInput.dataset.cartQuantity);

      // A 422 response only describes the inventory error; it does not include
      // the server's canonical cart. Shopify has already capped the line to the
      // available inventory, so reconcile the whole section (quantity, price,
      // grams, subtotal) and re-run the THC limiter from the real cart without
      // requiring a full page refresh.
      const recoveryVersion = ++this.#quantityRecoveryVersion;
      this.#syncRejectedQuantity(line, quantitySelector, quantityInput, recoveryVersion, message);
    } else {
      console.error('Quantity input not found');
    }

    this.#showLineError(line, message);

    // Dispatch unconditionally, even if some refs were missing above, so listeners
    // (e.g. the cart drawer's stuck-spinner cleanup) can still recover.
    this.dispatchEvent(
      new CartErrorEvent(this.sectionId, parsedResponseText.message, parsedResponseText.description, parsedResponseText.errors)
    );
  };

  /**
   * Whether this component is the cart drawer rather than the full cart page.
   * @returns {boolean}
   */
  #isCartDrawer() {
    return Boolean(this.closest(CART_DRAWER_SELECTOR));
  }

  /**
   * Get the response HTML that belongs to this cart surface.
   * Quantity-only drawer changes use the lightweight fragment instead of the
   * recommendation-owning footer section.
   * @param {Record<string, string>|undefined} sections
   * @param {boolean} useDrawerFragment
   * @returns {string|undefined}
   */
  #getSectionHTML(sections, useDrawerFragment) {
    const responseSectionId = useDrawerFragment && this.#isCartDrawer()
      ? OCS_CART_DRAWER_FRAGMENT_SECTION_ID
      : this.sectionId;
    return sections?.[responseSectionId];
  }

  /**
   * Apply authoritative cart HTML while preserving recommendations on the
   * quantity-only path. Product membership has not changed, so cloning the
   * current mobile upsell into the fragment avoids unnecessary replacement;
   * the desktop upsell is outside this component and is untouched.
   * @param {string} sectionHTML
   * @param {boolean} useDrawerFragment
   */
  #morphCartItems(sectionHTML, useDrawerFragment) {
    if (!useDrawerFragment || !this.#isCartDrawer()) {
      morphSection(this.sectionId, sectionHTML);
      return;
    }

    const fragment = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const nextCartItems = fragment.querySelector('cart-items-component[data-ocs-cart-drawer-fragment]');
    const currentMobileUpsell = this.querySelector('#ocs-upsell-tgt-mobile');
    const nextMobileUpsell = nextCartItems?.querySelector('#ocs-upsell-tgt-mobile');

    if (!(nextCartItems instanceof HTMLElement) || !nextMobileUpsell) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    if (currentMobileUpsell) {
      nextMobileUpsell.replaceWith(currentMobileUpsell.cloneNode(true));
    }

    nextCartItems.dataset.sectionId = this.sectionId;
    morph(this, nextCartItems);
  }

  /**
   * Collect section ids for every cart surface. Quantity-only changes replace
   * the drawer's expensive recommendation section with a lightweight dynamic
   * fragment while continuing to update the visible cart page, when present.
   * @param {boolean} [useDrawerFragment]
   * @returns {Set<string>}
   */
  #getSectionsToUpdate(useDrawerFragment = false) {
    const sections = new Set();
    const addSection = (item) => {
      if (!(item instanceof HTMLElement) || !item.dataset.sectionId) return;

      if (useDrawerFragment && item.closest(CART_DRAWER_SELECTOR)) {
        sections.add(OCS_CART_DRAWER_FRAGMENT_SECTION_ID);
        return;
      }

      sections.add(item.dataset.sectionId);
    };

    addSection(this);
    document.querySelectorAll('cart-items-component').forEach(addSection);
    return sections;
  }

  /**
   * Shows a line-item error message. Also used to re-apply the message after a
   * section morph re-renders the (hidden, empty) error container.
   * @param {number} line
   * @param {string} message
   */
  #showLineError(line, message) {
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (cartItemError instanceof HTMLElement && cartItemErrorContainer instanceof HTMLElement) {
      cartItemError.textContent = message;
      cartItemErrorContainer.classList.remove('hidden');
    } else {
      console.error('Cart item error ref(s) not found');
    }
  }

  /**
   * Set a cart quantity input to a confirmed quantity and refresh its controls.
   * @param {HTMLElement|undefined} quantitySelector
   * @param {HTMLInputElement} quantityInput
   * @param {string|number|undefined} quantity
   */
  #setRejectedQuantity(quantitySelector, quantityInput, quantity) {
    const numericQuantity = Number.parseInt(String(quantity), 10);
    if (!Number.isFinite(numericQuantity)) return;

    const value = String(numericQuantity);
    quantityInput.value = value;
    quantityInput.defaultValue = value;
    quantityInput.dataset.cartQuantity = value;

    if (quantitySelector && typeof quantitySelector.updateButtonStates === 'function') {
      quantitySelector.updateButtonStates();
    }
  }

  /**
   * Reconcile the UI after a rejected quantity change. Shopify caps the line to
   * the available inventory even on a 422, so a no-op `cart/update` (empty body +
   * `sections`) returns the authoritative cart and freshly rendered sections
   * without mutating anything. Morphing those sections updates the quantity,
   * price, grams and subtotal, and dispatching `cart:update` re-runs the THC
   * gram limiter / checkout over-limit state that the error path used to skip.
   * @param {number} line
   * @param {HTMLElement|undefined} quantitySelector
   * @param {HTMLInputElement} quantityInput
   * @param {number} recoveryVersion
   * @param {string} message - Inventory error to re-show after the morph.
   */
  async #syncRejectedQuantity(line, quantitySelector, quantityInput, recoveryVersion, message) {
    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          sections: Array.from(this.#getSectionsToUpdate()),
          sections_url: window.location.pathname,
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, config);
      if (!response.ok) return;

      const cart = await response.json();
      if (recoveryVersion !== this.#quantityRecoveryVersion || !quantityInput.isConnected) return;

      // Reconcile the whole line from the authoritative render. Fall back to just
      // the numeric quantity if the section HTML is unexpectedly missing.
      const selfSectionHtml = cart.sections?.[this.sectionId];
      if (selfSectionHtml) {
        morphSection(this.sectionId, selfSectionHtml);
      } else {
        const lineItemKey = this.refs.cartItemRows[line - 1]?.dataset.key;
        const item = lineItemKey
          ? cart.items?.find((cartItem) => cartItem.key === lineItemKey)
          : cart.items?.[line - 1];
        if (item) this.#setRejectedQuantity(quantitySelector, quantityInput, item.quantity);
      }

      // The morph re-renders the row with the error container hidden/empty, so
      // re-show the inventory message on the fresh DOM.
      this.#showLineError(line, message);

      // Broadcast so every gram meter, the header chip, the subtotal and the
      // checkout over-limit state recalculate from the reconciled cart.
      this.dispatchEvent(
        new CartUpdateEvent(cart, this.sectionId, {
          itemCount: Number(cart.item_count) || 0,
          source: 'cart-items-component',
          sections: cart.sections,
        })
      );

      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      console.error('Failed to restore rejected cart quantity:', error);
    }
  }

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const useDrawerFragment = event.detail.data.usesCartDrawerFragment === true;
    const cartItemsHtml = this.#getSectionHTML(event.detail.data.sections, useDrawerFragment);
    if (cartItemsHtml) {
      this.#morphCartItems(cartItemsHtml, useDrawerFragment);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
